import { Tooltip, TruncatedText } from '@/shared/ui';
import { AlertCircle } from 'lucide-react';
import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ConversationTranscript, type DialogEventFilter, PERSON_ROLES, PHRASE_TYPES } from '@/entities/conversations';
import { useTimeDisplayFormat, useTranscriptEventFilters } from '@/entities/settings';
import { useDialogSessionOptional } from '@/features/DialogSession';
import { cn } from '@/shared/lib';
import { formatTimeInterval, withSecondsLabel } from '@/shared/lib/utils/string';

type Props = {
  transcriptData: ConversationTranscript[];
  audioDuration: number;
};

type InterruptionInterval = {
  start: number; // в миллисекундах
  end: number; // в миллисекундах
  interruptedPhraseId?: number | null; // ID перебитой фразы
  interruptionPhraseId?: number | null; // ID перебивающей фразы
  /** Роль перебивающего (взято из фразы interruptionPhraseId). От неё зависит цвет маркера */
  interrupterRole?: string;
};

/**
 * Проверяет, должен ли маркер показываться при текущем наборе фильтров.
 * - 'interruption' (без подпунктов) — показываются все
 * - 'interruption_client' — только клиентские
 * - 'interruption_employee' — только консультантские
 */
const isVisibleForFilters = (interrupterRole: string | undefined, filters: DialogEventFilter[]): boolean => {
  if (filters.includes('interruption')) return true;
  if (interrupterRole === PERSON_ROLES.CLIENT && filters.includes('interruption_client')) return true;
  if (interrupterRole === PERSON_ROLES.EMPLOYEE && filters.includes('interruption_employee')) return true;
  return false;
};

/**
 * Компонент для отображения интервалов прерываний поверх визуализатора аудио.
 * Цвета маркеров зависят от роли перебивающего: консультант — красный, клиент — жёлтый.
 * Клик по маркеру скроллит чат к фразе перебивающего.
 */
export const InterruptionIntervals: FC<Props> = ({ transcriptData, audioDuration }) => {
  const { t } = useTranslation(['common', 'dialogs']);
  // Границы перебивания сопоставляются с временем плеера, у оси которого показана дорожка, — формат общий с ним
  const timeDisplayFormat = useTimeDisplayFormat('player');
  const timeLabel = withSecondsLabel(t('dialogs:timeLabel'), timeDisplayFormat, t('common:secondsSuffix'));
  // Отбор событий — из настроек пользователя, того же источника, что и у ленты реплик
  const { eventFilters } = useTranscriptEventFilters();
  // Сессия нужна только для перехода к реплике по клику на маркер
  const dialogSession = useDialogSessionOptional();

  const totalDurationMs = audioDuration * 1000;

  // Map для быстрого поиска фраз по ID
  const phrasesMap = useMemo(() => {
    return new Map(transcriptData.map((msg) => [msg.id, msg]));
  }, [transcriptData]);

  const interruptionIntervals = useMemo<InterruptionInterval[]>(() => {
    if (!transcriptData.length) return [];

    return transcriptData
      .filter((message) => message.type === PHRASE_TYPES.INTERRUPTION)
      .filter((message) => message.startedAt != null)
      .map((message) => {
        const interrupter = message.interruptionPhraseId ? phrasesMap.get(message.interruptionPhraseId) : undefined;
        return {
          start: message.startedAt,
          // Событие без конца отмечается по своему началу: отбросив перебивание за нулевую
          // длительность, шкала разошлась бы числом отметок с речевой аналитикой
          end: message.finishedAt ?? message.startedAt,
          interruptedPhraseId: message.interruptedPhraseId,
          interruptionPhraseId: message.interruptionPhraseId,
          interrupterRole: interrupter?.person?.role,
        };
      });
  }, [transcriptData, phrasesMap]);

  const visibleIntervals = useMemo(
    () => interruptionIntervals.filter((interval) => isVisibleForFilters(interval.interrupterRole, eventFilters)),
    [interruptionIntervals, eventFilters],
  );

  if (!totalDurationMs || visibleIntervals.length === 0) {
    return null;
  }

  const handleMarkerClick = (interruptionPhraseId: number | null | undefined) => {
    if (!interruptionPhraseId || !dialogSession) return;
    // По требованию: клик скроллит к фразе перебивающего (фраза определяет цвет маркера)
    dialogSession.scrollToTranscript(String(interruptionPhraseId));
  };

  return (
    <div className="absolute inset-0 pointer-events-none z-10">
      {visibleIntervals.map((interval, index) => {
        const startPercent = (interval.start / totalDurationMs) * 100;
        const widthPercent = ((interval.end - interval.start) / totalDurationMs) * 100;
        const startSeconds = interval.start / 1000;
        const endSeconds = interval.end / 1000;
        const startTime = formatTimeInterval(startSeconds, timeDisplayFormat);
        const endTime = formatTimeInterval(endSeconds, timeDisplayFormat);

        const interruptedPhrase = interval.interruptedPhraseId ? phrasesMap.get(interval.interruptedPhraseId) : null;
        const interruptingPhrase = interval.interruptionPhraseId ? phrasesMap.get(interval.interruptionPhraseId) : null;

        const interruptedText = interruptedPhrase?.text || '';
        const interruptingText = interruptingPhrase?.text || '';

        // Перебивающий клиент → жёлтый маркер, иначе (консультант/неизвестно) → красный
        const isClientInterrupter = interval.interrupterRole === PERSON_ROLES.CLIENT;
        const intervalBgClass = isClientInterrupter
          ? 'bg-warning/30 border-l border-r border-warning/50'
          : 'bg-destructive/30 border-l border-r border-destructive/50';
        const markerColorClass = isClientInterrupter
          ? 'bg-warning text-warning-foreground'
          : 'bg-destructive text-destructive-foreground';

        return (
          <div
            key={index}
            // Минимальная ширина — ради перебивания в доли секунды: по своей длительности
            // оно заняло бы доли пикселя и осталось бы незаметным рядом с длинными
            className={cn('absolute top-0 bottom-0 rounded-md min-w-0.5', intervalBgClass)}
            data-interval-ms={`${interval.start}-${interval.end}`}
            data-interrupter-role={interval.interrupterRole ?? ''}
            style={{
              left: `${startPercent}%`,
              width: `${widthPercent}%`,
            }}
          >
            {/* Кружок с восклицательным знаком сверху интервала */}
            <div className="absolute -top-2 left-1/2 -translate-x-1/2 pointer-events-auto">
              <Tooltip
                tooltip={
                  <div className="text-sm space-y-2 max-w-xs">
                    <div className="font-medium mb-1">{t('dialogs:interruption')}</div>
                    <div className="text-muted-foreground">
                      {t('dialogs:interruptionTime', { label: timeLabel, start: startTime, end: endTime })}
                    </div>
                    {interruptedText && (
                      <div className="pt-2 border-t border-border">
                        <div className="font-medium mb-1 text-xs">{t('dialogs:interruptedPhrase')}</div>
                        <TruncatedText lines={2} className="text-muted-foreground text-xs">
                          {interruptedText}
                        </TruncatedText>
                      </div>
                    )}
                    {interruptingText && (
                      <div className="pt-2 border-t border-border">
                        <div className="font-medium mb-1 text-xs">{t('dialogs:interruptingPhrase')}</div>
                        <TruncatedText lines={2} className="text-muted-foreground text-xs">
                          {interruptingText}
                        </TruncatedText>
                      </div>
                    )}
                  </div>
                }
              >
                <div
                  role="button"
                  tabIndex={-1}
                  aria-label={t('dialogs:interruption')}
                  onClick={() => handleMarkerClick(interval.interruptionPhraseId)}
                  className={cn(
                    'flex items-center justify-center size-5 rounded-full shadow-sm cursor-pointer',
                    markerColorClass,
                  )}
                >
                  <AlertCircle className="size-3" />
                </div>
              </Tooltip>
            </div>
          </div>
        );
      })}
    </div>
  );
};
